import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useInvalidate,
  useList,
  useNavigation,
  type CrudFilter,
  type CrudSort,
} from '@refinedev/core';
import {
  Alert,
  Box, Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow,
  Toolbar, Tooltip, Typography, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { TableNotification } from '@/components/shared/TableNotification';
import { UploadDialog, UploadDialogResult } from '@/components/shared/UploadDialog';
import { LoadingButton } from '@/components/shared/LoadingButton';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import {
  SortFilterOption,
  SortFilterValue,
} from '@/components/shared/SortFilterDialog';
import { SortFilterControl } from '@/components/shared/SortFilterControl';
import { useUser } from '@/context/UserContext';
import { logger } from '@/services/logging';
import type { Order } from '@/types';
import { StatusSelect } from '@/components/shared/StatusSelect';

type OrderListRow = Order & {
  creatorId?: string;
  orderDate?: string;
  movementType?: string;
  preCyberOrderNumber?: string;
  orderCreatorName?: string;
  orderPlacedDate?: string;
  orderChangedDate?: string;
  orderStatus?: string;
  statusDate?: string;
  contactName?: string;
  customerSoldToName?: string;
  soldToName?: string;
  shippingCondition?: string;
  shippingConditionDescription?: string;
  cyberOrderStatus?: string;
  shipTo?: string;
  shipToNumber?: string;
  shipToCity?: string;
  shipToPlantCity?: string;
  shipToPlantCode?: string;
  plantCode?: string;
  supplyPointId?: string;
  routeCode?: string;
};

type PendingStatusNoteChange = {
  order: OrderListRow;
  status: string;
};

const COLUMNS = [
  { id: 'orderNumber',           label: 'Order #',          sortable: true  },
  { id: 'preCyberOrderNumber',   label: 'Pre-BCP Order #', sortable: true  },
  { id: 'creatorId',             label: 'Order Creator',    sortable: true  },
  { id: 'orderType',             label: 'Type',             sortable: true  },
  { id: 'orderDate',             label: 'Creation Date',    sortable: true  },
  { id: 'movementType',          label: 'Shipping Condition', sortable: true  },
  { id: 'requestedDeliveryDate', label: 'Req. Delv. Date',  sortable: true  },
  { id: 'shipToPlantCode',       label: 'Supply Point',     sortable: true  },
  { id: 'contactName',           label: 'Sold-to Name',     sortable: true  },
  { id: 'shipToNumber',          label: 'Ship-to Code',     sortable: true  },
  { id: 'shipToPlantCity',       label: 'Ship-to City',     sortable: true  },
  { id: 'orderStatus',           label: 'Order Status',     sortable: true  },
  { id: 'statusDate',            label: 'Status Date',      sortable: true  },
];

const HEADER_IDS_BY_COLUMN_ID: Record<string, string> = {
  orderNumber: 'qa-orders-orderNumber-hdr',
  preCyberOrderNumber: 'qa-orders-preCyberOrderNumber-hdr',
  creatorId: 'qa-orders-creatorId-hdr',
  orderType: 'qa-orders-type-hdr',
  orderDate: 'qa-orders-orderDate-hdr',
  movementType: 'qa-orders-movementType-hdr',
  requestedDeliveryDate: 'qa-orders-requestedDeliveryDate-hdr',
  shipToPlantCode: 'qa-orders-plantCode-hdr',
  contactName: 'qa-orders-contactNam-hdr',
  shipToNumber: 'qa-orders-shipToNumber-hdr',
  shipToPlantCity: 'qa-orders-shipToPlantCity-hdr',
  orderStatus: 'qa-orders-orderStatus-hdr',
  statusDate: 'ga-orders-statusDate-hdr',
};

const AVAILABLE_COLUMN_IDS = COLUMNS.map((column) => column.id);
const COLUMNS_BY_ID = Object.fromEntries(COLUMNS.map((column) => [column.id, column])) as Record<string, (typeof COLUMNS)[number]>;

const BULK_ORDER_EXPORT_COLUMNS = [
  ...COLUMNS,
  { id: 'requestedPickupDate', label: 'Req. Pickup Date' },
  { id: 'purchaseOrderNumber', label: 'Purchase Order #' },
  { id: 'soldToNumber', label: 'Sold-to Code' },
  { id: 'shipToName', label: 'Ship-to Name' },
  { id: 'shippingCondition', label: 'Shipping Condition' },
  { id: 'modeOfTransportation', label: 'Mode of Transportation' },
  { id: 'routeCode', label: 'Route Code' },
  { id: 'materialCode', label: 'Material Code' },
  { id: 'materialDescription', label: 'Material Description' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'unitOfMeasure', label: 'Unit of Measure' },
  { id: 'customerMaterialNumber', label: 'Customer Material #' },
  { id: 'gomMaterialCode', label: 'GOM Material Code' },
] as const;

const DEFAULT_ORDER_STATUSES = ['OC-Draft', 'MOT-Scheduled', 'SP-Loaded'];
const DOWNLOAD_ORDER_STATUSES = [
  'IM-Confirmed',
  'IM-Confirmed w/o RC',
  'IM-Recycle',
  'IM-Reject',
  'MOT-Pending carrier',
  'MOT-Recycle',
  'MOT-Reject',
  'MOT-Scheduled',
  'MOT-Scheduled/updated',
  'OC-Cancelled',
  'OC-Draft',
  'OC-New',
  'OC-New/Updated',
  'OC-Recycle Bad RC',
  'OC-Returned',
  'OC-Revised MOT',
  'SP-Loaded',
  'SP-Received',
  'SP-Recycle',
];
const NOTE_REQUIRED_STATUSES = ['IM-Recycle', 'IM-Reject', 'MOT-Recycle', 'MOT-Reject', 'SP-Recycle', 'SP-Reject'];
const EMPTY_ROUTE_ERROR_MESSAGE = 'The route code of the order is empty. Please search and select a route before changing order status.';

const uniqueStatuses = (items: (string | null | undefined)[]) =>
  Array.from(
    new Set(
      items
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
    ),
  );

const toStatusList = (payload: unknown): string[] => {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown[] })?.data)
      ? ((payload as { data?: unknown[] }).data ?? [])
      : [];

  return uniqueStatuses(
    raw.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'orderStatus' in item) {
        return (item as { orderStatus?: string }).orderStatus;
      }
      if (item && typeof item === 'object' && 'cyberOrderStatus' in item) {
        return (item as { cyberOrderStatus?: string }).cyberOrderStatus;
      }
      return undefined;
    }),
  );
};

const hasStatusPermission = (payload: unknown): boolean => {
  if (payload && typeof payload === 'object' && 'Permission' in payload) {
    return Boolean((payload as { Permission?: unknown }).Permission);
  }

  return true;
};

const ORDER_ROW_HOVER_BG = '#E3F2FC';
const ORDER_LINK_COLOR = '#0c479d';
const EXPORT_PAGE_SIZE = 500;
const DOWNLOAD_PAGE_SIZE = 5000;
const toOrderListApiFieldName = (field: string) => (field === 'orderDate' ? 'OrderPlacedDate' : field);
const OUTLINED_ACTION_BUTTON_SX = {
  maxHeight: '36px',
  letterSpacing: '0.08em',
  color: '#0c479d',
  fontWeight: 700,
  borderColor: 'rgba(0, 0, 0, 0.12)',
  transition: 'none !important',
  animation: 'none !important',
};

const PAGE_HEADER_BUTTON_SX = {
  maxHeight: '36px',
  letterSpacing: '0.08em',
  color: '#000',
  backgroundColor: '#fff',
  borderColor: 'rgba(0, 0, 0, .12)',
  fontWeight: 700,
  textTransform: 'none',
  transition: 'none !important',
  animation: 'none !important',
  '&:hover': {
    borderColor: 'rgba(0, 0, 0, .12)',
    backgroundColor: '#fff',
  },
};

const SHIPPING_ICON_SX = {
  textAlign: 'left',
  color: 'var(--mat-icon-color, inherit)',
  fontFamily: 'Material Icons',
  fontWeight: 'normal',
  fontStyle: 'normal',
  fontSize: '24px',
  lineHeight: 1,
  letterSpacing: 'normal',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  wordWrap: 'normal',
  direction: 'ltr',
  WebkitFontFeatureSettings: 'liga',
  WebkitFontSmoothing: 'antialiased',
  userSelect: 'none',
  backgroundRepeat: 'no-repeat',
  display: 'inline-block',
  fill: 'currentColor',
  height: '24px',
  width: '24px',
  overflow: 'hidden',
  mt: '3px',
};

const removeRecordKey = <T extends string>(record: Record<string, T>, key: string) => {
  const next = { ...record };
  delete next[key];
  return next;
};

const truncate = (value?: string | null, max = 12) => {
  if (!value) return '-';
  return value.length > max ? `${value.slice(0, max)}...` : value;
};

const shouldShowTooltip = (value?: string | null, max = 12) => !!value && value.length > max;

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
};

const normalizeOrderIdentifier = (value?: string | null) => {
  if (!value) return '-';
  const normalized = value.replace(/\./g, '').trim();
  return normalized || '-';
};

const getOrderKey = (order: OrderListRow) =>
  String(order.orderId ?? order.orderNumber ?? '').trim();

const getOrderCreator = (order: OrderListRow) => order.orderCreatorName ?? order.creatorId ?? '';

const getOrderPlacedDateValue = (order: OrderListRow) => order.orderPlacedDate ?? order.orderDate ?? '';

const getRequestedDeliveryDateValue = (order: OrderListRow) => order.requestedDeliveryDate ?? '';

const firstNonEmptyValue = (...values: Array<string | null | undefined>) =>
  values.find((value) => typeof value === 'string' && value.trim().length > 0)?.trim() ?? '';

const getSupplyPointValue = (order: OrderListRow) =>
  firstNonEmptyValue(order.supplyPointId, order.supplyPointCode, order.plantCode);

const getSoldToNameValue = (order: OrderListRow) =>
  firstNonEmptyValue(order.customerSoldToName, order.soldToName, order.customerName, order.contactName);

const getShipToCodeValue = (order: OrderListRow) =>
  firstNonEmptyValue(order.shipToPlantCode, order.shipToNumber, order.shipTo);

const getShipToCityValue = (order: OrderListRow) =>
  firstNonEmptyValue(order.shipToPlantCity, order.shipToCity);

const getOrderStatusValue = (order: OrderListRow) =>
  order.orderStatus ?? order.cyberOrderStatus ?? (typeof order.status === 'string' ? order.status : '');

const getStatusDateValue = (order: OrderListRow) => order.statusDate ?? order.orderChangedDate ?? '';

const getMovementTypeLabel = (order: OrderListRow) => {
  const shippingConditionLabel = `${order.shippingCondition ?? ''} - ${order.shippingConditionDescription ?? ''}`.trim();

  if (shippingConditionLabel && shippingConditionLabel !== '-') {
    return shippingConditionLabel;
  }

  return order.movementType ?? '';
};

const movementIcon = (order: OrderListRow) => {
  const normalized = [
    order.movementType,
    order.modeOfTransportation,
    order.shippingCondition,
    order.shippingConditionDescription,
  ]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();

  if (normalized.includes('TRUCK')) return <LocalShippingIcon sx={SHIPPING_ICON_SX} />;
  if (normalized.includes('RAIL') || normalized.includes('TRAIN')) return <TrainIcon sx={SHIPPING_ICON_SX} />;
  if (normalized.includes('BARGE') || normalized.includes('BOAT')) return <DirectionsBoatIcon sx={SHIPPING_ICON_SX} />;
  return null;
};

export const OrderList: React.FC = () => {
  const { create } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { getBusinessUnit, userData } = useUser();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeSortFields, setActiveSortFields] = useState<SortFilterValue[]>([]);
  const [activeFilterFields, setActiveFilterFields] = useState<SortFilterValue[]>([]);
  const [isOrderReadyAfterStatusChange, setIsOrderReadyAfterStatusChange] = useState(true);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});
  const [statusDateOverrides, setStatusDateOverrides] = useState<Record<string, string>>({});
  const [availableOrderStatuses, setAvailableOrderStatuses] = useState<string[]>(DEFAULT_ORDER_STATUSES);
  const [multiSortOrders, setMultiSortOrders] = useState<OrderListRow[] | null>(null);
  const [isMultiSortLoading, setIsMultiSortLoading] = useState(false);
  const [statusChangeError, setStatusChangeError] = useState<string | null>(null);
  const [emptyRouteDialogOpen, setEmptyRouteDialogOpen] = useState(false);
  const [pendingStatusNoteChange, setPendingStatusNoteChange] = useState<PendingStatusNoteChange | null>(null);
  const [statusNote, setStatusNote] = useState('');

  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRPP]     = useState(25);
  const [sortField, setSortField] = useState('orderDate');
  const [sortDir, setSortDir]     = useState<'asc' | 'desc'>('desc');
  const businessUnit = getBusinessUnit();

  const sortFilterFields = useMemo<SortFilterOption[]>(
    () => COLUMNS.map((column) => ({ property: column.id, label: column.label })),
    [],
  );

  const appliedSorters = useMemo<CrudSort[]>(() => (
    activeSortFields.length > 0
      ? activeSortFields.map<CrudSort>((item) => ({
        field: toOrderListApiFieldName(item.property),
        order: item.value === 'ASC' ? 'asc' : 'desc',
      }))
      : [{ field: toOrderListApiFieldName(sortField), order: sortDir as 'asc' | 'desc' }]
  ), [activeSortFields, sortDir, sortField]);

  const appliedFilters = useMemo<CrudFilter[]>(() => (
    activeFilterFields.map<CrudFilter>((item) => ({
      field: toOrderListApiFieldName(item.property),
      operator: 'contains',
      value: item.value,
    }))
  ), [activeFilterFields]);

  const sortParams = useMemo(() => appliedSorters.map((sorter) => ({
    columnName: sorter.field,
    value: sorter.order === 'asc' ? 'ASC' : 'DESC',
  })), [appliedSorters]);

  const filterParams = useMemo(() => appliedFilters.map((filter) => ({
    columnName: 'field' in filter ? filter.field : '',
    value: 'value' in filter ? filter.value : '',
  })), [appliedFilters]);

  const { data, isLoading, isError, refetch } = useList<OrderListRow>({
    resource: 'simpleorder',
    pagination: { current: page + 1, pageSize: rowsPerPage },
    sorters: appliedSorters,
    filters: appliedFilters,
    queryOptions: {
      staleTime: 0,
      cacheTime: 0,
    },
    meta: {
      extraPayload: {
        source: businessUnit,
      },
      requestOptions: {
        noCache: true,
      },
    },
  });

  const orders: OrderListRow[] = (data?.data ?? []) as OrderListRow[];
  const total  = data?.total ?? 0;
  const shouldUseMultiSortDataset = activeSortFields.length > 1;
  const isTableLoading = isLoading || isMultiSortLoading || isRefreshing;
  const isRowUpdating = useCallback(
    (orderId: string) => !isOrderReadyAfterStatusChange && orderId === currentOrderId,
    [currentOrderId, isOrderReadyAfterStatusChange],
  );

  const getComparableOrderValue = (order: OrderListRow, property: string): string | number => {
    const effectiveStatusDate = statusDateOverrides[getOrderKey(order)] ?? getStatusDateValue(order);

    switch (property) {
      case 'orderNumber':
        return normalizeOrderIdentifier(order.orderNumber ?? order.orderId);
      case 'preCyberOrderNumber':
        return normalizeOrderIdentifier(order.preCyberOrderNumber);
      case 'creatorId':
        return getOrderCreator(order);
      case 'orderType':
        return order.orderTypeDescription ?? order.orderType ?? '';
      case 'orderDate': {
        const effectiveOrderDate = getOrderPlacedDateValue(order);
        const parsed = dayjs(effectiveOrderDate);
        return parsed.isValid() ? parsed.valueOf() : effectiveOrderDate;
      }
      case 'movementType':
        return `${getMovementTypeLabel(order)} ${order.modeOfTransportation ?? ''}`.trim();
      case 'requestedDeliveryDate': {
        const requestedDeliveryDate = getRequestedDeliveryDateValue(order);
        const parsed = dayjs(requestedDeliveryDate);
        return parsed.isValid() ? parsed.valueOf() : requestedDeliveryDate;
      }
      case 'shipToPlantCode':
        return getSupplyPointValue(order);
      case 'contactName':
        return getSoldToNameValue(order);
      case 'shipToNumber':
        return getShipToCodeValue(order);
      case 'shipToPlantCity':
        return getShipToCityValue(order);
      case 'orderStatus':
        return statusOverrides[getOrderKey(order)] ?? getOrderStatusValue(order);
      case 'statusDate': {
        const parsed = dayjs(effectiveStatusDate);
        return parsed.isValid() ? parsed.valueOf() : effectiveStatusDate;
      }
      default: {
        const value = (order as unknown as Record<string, unknown>)[property];
        return typeof value === 'number' || typeof value === 'string' ? value : '';
      }
    }
  };

  const displayedOrders = useMemo(() => {
    const sorts = activeSortFields.length > 0
      ? activeSortFields.map((item) => ({ property: item.property, direction: item.value === 'ASC' ? 'asc' : 'desc' as const }))
      : [{ property: sortField, direction: sortDir }];

    const sourceOrders = shouldUseMultiSortDataset && multiSortOrders ? multiSortOrders : orders;

    const sortedOrders = [...sourceOrders].sort((left, right) => {
      for (const sort of sorts) {
        const leftValue = getComparableOrderValue(left, sort.property);
        const rightValue = getComparableOrderValue(right, sort.property);

        if (leftValue === rightValue) {
          continue;
        }

        const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });

        if (comparison !== 0) {
          return sort.direction === 'asc' ? comparison : -comparison;
        }
      }

      return 0;
    });

    if (shouldUseMultiSortDataset) {
      const start = page * rowsPerPage;
      return sortedOrders.slice(start, start + rowsPerPage);
    }

    return sortedOrders;
  }, [activeSortFields, multiSortOrders, orders, page, rowsPerPage, shouldUseMultiSortDataset, sortDir, sortField, statusDateOverrides, statusOverrides]);

  const handleSort = (field: string) => {
    setActiveSortFields([]);
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
    setPage(0);
  };

  const getSortStateForField = (property: string) => {
    const activeSortIndex = activeSortFields.findIndex((item) => item.property === property);

    if (activeSortIndex >= 0) {
      return {
        active: true,
        direction: activeSortFields[activeSortIndex].value === 'ASC' ? 'asc' : 'desc',
        priority: activeSortIndex + 1,
      } as const;
    }

    if (sortField === property) {
      return {
        active: true,
        direction: sortDir,
        priority: 1,
      } as const;
    }

    return {
      active: false,
      direction: undefined,
      priority: undefined,
    } as const;
  };

  const isFieldActive = (property: string) =>
    getSortStateForField(property).active
    || activeFilterFields.some((item) => item.property === property);

  const clearStatusResetTimeout = () => undefined;

  const addOrderStatusNote = useCallback(async (order: OrderListRow, note: string) => {
    await axiosInstance.patch(
      '/note',
      {
        orderNumber: order.orderNumber ?? order.orderId,
        text: note,
      },
      { headers: jsonHeaders() },
    );
  }, []);

  const persistOrderStatusChange = useCallback(async (order: OrderListRow, status: string, note?: string) => {
    const orderId = getOrderKey(order);
    const changedAt = new Date().toISOString();

    setStatusChangeError(null);
    clearStatusResetTimeout();
    setIsOrderReadyAfterStatusChange(false);
    setCurrentOrderId(orderId);
    setStatusOverrides((prev) => ({ ...prev, [orderId]: status }));

    try {
      if (note) {
        await addOrderStatusNote(order, note);
      }

      await axiosInstance.post(
        '/Order/update',
        [{
          orderChangedDate: changedAt,
          orderPlacedDate: getOrderPlacedDateValue(order) || null,
          orderNumber: order.orderNumber ?? order.orderId ?? null,
          cyberOrderStatus: status || null,
          source: businessUnit,
        }],
        { headers: jsonHeaders() },
      );

      setStatusDateOverrides((prev) => ({ ...prev, [orderId]: changedAt }));

      await invalidate({
        resource: 'simpleorder',
        invalidates: ['list'],
      });
      await refetch();
    } catch (error) {
      setStatusOverrides((prev) => removeRecordKey(prev, orderId));
      setStatusDateOverrides((prev) => removeRecordKey(prev, orderId));
      setStatusChangeError('Failed to update the order status. Please try again.');
      logger.error('Failed to update order status', error as Error);
    } finally {
      setIsOrderReadyAfterStatusChange(true);
      setCurrentOrderId(null);
    }
  }, [addOrderStatusNote, businessUnit, invalidate, refetch]);

  const handleStatusChange = (order: OrderListRow, status: string) => {
    if (!order.routeCode?.trim()) {
      setEmptyRouteDialogOpen(true);
      return;
    }

    if (NOTE_REQUIRED_STATUSES.includes(status)) {
      setPendingStatusNoteChange({ order, status });
      setStatusNote('');
      return;
    }

    void persistOrderStatusChange(order, status);
  };

  const handleNoteDialogClose = () => {
    if (isOrderReadyAfterStatusChange) {
      setPendingStatusNoteChange(null);
      setStatusNote('');
    }
  };

  const handleNoteDialogSubmit = async () => {
    if (!pendingStatusNoteChange || !statusNote.trim()) {
      return;
    }

    await persistOrderStatusChange(
      pendingStatusNoteChange.order,
      pendingStatusNoteChange.status,
      statusNote.trim(),
    );

    setPendingStatusNoteChange(null);
    setStatusNote('');
  };

  useEffect(() => () => {
    clearStatusResetTimeout();
  }, []);

  useEffect(() => {
    const loadOrderStatuses = async () => {
      try {
        const { data: response } = await axiosInstance.get('/cyberorderstatus', {
          headers: jsonHeaders(),
        });

        if (!hasStatusPermission(response)) {
          setAvailableOrderStatuses(DEFAULT_ORDER_STATUSES);
          return;
        }

        const endpointStatuses = toStatusList(response);
        if (endpointStatuses.length > 0) {
          setAvailableOrderStatuses(endpointStatuses);
        } else {
          setAvailableOrderStatuses(DEFAULT_ORDER_STATUSES);
        }
      } catch (error) {
        setAvailableOrderStatuses(DEFAULT_ORDER_STATUSES);
        logger.error('Failed to load order statuses from /cyberorderstatus', error as Error);
      }
    };

    void loadOrderStatuses();
  }, [businessUnit]);

  const loadMultiSortOrders = useCallback(async () => {
    if (!shouldUseMultiSortDataset) {
      setMultiSortOrders(null);
      setIsMultiSortLoading(false);
      return;
    }

    setIsMultiSortLoading(true);

    try {
      const allOrders: OrderListRow[] = [];
      let pageNumber = 1;
      let totalCount = Number.POSITIVE_INFINITY;

      while (allOrders.length < totalCount) {
        const payload = {
          PageNumber: pageNumber,
          LinesPerPage: EXPORT_PAGE_SIZE,
          Sorting: sortParams,
          Filters: filterParams,
        };

        const { data: response } = await axiosInstance.get('/simpleorder', {
          params: payload,
          headers: jsonHeaders(),
        });

        const rows = (response?.data ?? []) as OrderListRow[];
        const count = Number(response?.count ?? rows.length);

        allOrders.push(...rows);
        totalCount = count;

        if (rows.length === 0) {
          break;
        }

        pageNumber += 1;
      }

      setMultiSortOrders(allOrders);
    } catch (error) {
      logger.error('Failed to load full order set for multi-column sorting', error as Error);
      setMultiSortOrders(null);
    } finally {
      setIsMultiSortLoading(false);
    }
  }, [businessUnit, filterParams, shouldUseMultiSortDataset, sortParams]);

  useEffect(() => {
    if (!shouldUseMultiSortDataset) {
      setMultiSortOrders(null);
      setIsMultiSortLoading(false);
      return;
    }

    void loadMultiSortOrders().catch(() => undefined);
  }, [loadMultiSortOrders, shouldUseMultiSortDataset]);

  const handleRefreshTable = async () => {
    setIsRefreshing(true);

    try {
      await invalidate({
        resource: 'simpleorder',
        invalidates: ['list'],
      });

      await refetch();

      if (shouldUseMultiSortDataset) {
        await loadMultiSortOrders();
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const shouldRefreshOrdersList = Boolean((location.state as { refreshOrdersList?: boolean } | null)?.refreshOrdersList);

    if (!shouldRefreshOrdersList) {
      return;
    }

    void handleRefreshTable();

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [handleRefreshTable, location.pathname, location.search, location.state, navigate]);

  const orderStatuses = useMemo(
    () => uniqueStatuses([
      ...availableOrderStatuses,
      ...orders.map((item) => getOrderStatusValue(item)),
      ...Object.values(statusOverrides),
    ]),
    [availableOrderStatuses, orders, statusOverrides],
  );

  const handleSortFilterApply = (items: SortFilterValue[]) => {
    setActiveSortFields(items);
    if (items.length > 0) {
      const firstSorter = items[0];
      setSortField(firstSorter.property);
      setSortDir(firstSorter.value === 'ASC' ? 'asc' : 'desc');
    }

    setPage(0);
  };

  const handleFilterApply = (items: SortFilterValue[]) => {
    setActiveFilterFields(items);
    setPage(0);
  };

  const getExportValue = (columnId: string, order: OrderListRow) => {
    const orderKey = getOrderKey(order);
    const products = Array.isArray(order.products) ? order.products : [];
    const firstProduct = products[0] as (OrderListRow['products'][number] & Record<string, unknown>) | undefined;

    switch (columnId) {
      case 'orderNumber':
        return normalizeOrderIdentifier(order.orderNumber ?? order.orderId);
      case 'preCyberOrderNumber':
        return normalizeOrderIdentifier(order.preCyberOrderNumber);
      case 'creatorId':
        return getOrderCreator(order) || '-';
      case 'orderType':
        return order.orderTypeDescription ?? order.orderType ?? '-';
      case 'orderDate':
        return formatDate(getOrderPlacedDateValue(order));
      case 'movementType':
        return getMovementTypeLabel(order) || '-';
      case 'contactName':
        return getSoldToNameValue(order) || '-';
      case 'shipToPlantCode':
        return getSupplyPointValue(order) || '-';
      case 'shipToNumber':
        return getShipToCodeValue(order) || '-';
      case 'shipToPlantCity':
        return getShipToCityValue(order) || '-';
      case 'requestedDeliveryDate':
        return formatDate(getRequestedDeliveryDateValue(order));
      case 'orderStatus':
        return statusOverrides[orderKey] ?? getOrderStatusValue(order) ?? '-';
      case 'statusDate':
        return formatDate(statusDateOverrides[orderKey] ?? getStatusDateValue(order));
      case 'requestedPickupDate':
        return formatDate(order.requestedPickupDate);
      case 'purchaseOrderNumber':
        return order.purchaseOrderNumber ?? '';
      case 'soldToNumber':
        return order.soldToNumber ?? order.customerId ?? '';
      case 'shipToName':
        return order.shipToName ?? '';
      case 'shippingCondition':
        return order.shippingConditionDescription
          ? `${order.shippingCondition ?? ''} - ${order.shippingConditionDescription}`.trim()
          : order.shippingCondition ?? '';
      case 'modeOfTransportation':
        return order.modeOfTransportation ?? '';
      case 'routeCode':
        return order.routeCode ?? '';
      case 'materialCode':
        return firstProduct?.materialCode ?? '';
      case 'materialDescription':
        return firstProduct?.materialDescription ?? firstProduct?.description ?? '';
      case 'quantity':
        return firstProduct?.quantity ?? '';
      case 'unitOfMeasure':
        return firstProduct?.salesUOM ?? firstProduct?.unitOfMeasure ?? '';
      case 'customerMaterialNumber':
        return firstProduct?.customerMaterialNumber ?? '';
      case 'gomMaterialCode':
        return firstProduct?.gomMaterialCode ?? '';
      default:
        return '-';
    }
  };

  const getExportRows = (ordersToExport: OrderListRow[]) => ordersToExport.flatMap((order) => {
    const products = Array.isArray(order.products) ? order.products : [];

    if (products.length <= 1) {
      return [order];
    }

    return products.map((product) => ({
      ...order,
      products: [product],
    }));
  });

  const toCsvCell = (value: unknown) => {
    const stringValue = String(value ?? '');
    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const fetchAllOrdersForExport = async () => {
    const allOrders: OrderListRow[] = [];
    let pageNumber = 1;
    let totalCount = Number.POSITIVE_INFINITY;

    while (allOrders.length < totalCount) {
      const payload = {
        PageNumber: pageNumber,
        LinesPerPage: EXPORT_PAGE_SIZE,
        Sorting: sortParams,
        Filters: filterParams,
      };

      const { data: response } = await axiosInstance.get('/simpleorder', {
        params: payload,
        headers: jsonHeaders(),
      });

      const rows = (response?.data ?? []) as OrderListRow[];
      const count = Number(response?.count ?? rows.length);

      allOrders.push(...rows);
      totalCount = count;

      if (rows.length === 0) {
        break;
      }

      pageNumber += 1;
    }

    return allOrders;
  };

  const handleExport = async () => {
    setIsExtracting(true);
    try {
      const payload = {
        UserId: userData.userId,
        ProductType: null,
        Source: businessUnit,
        RecordId: null,
        SearchFilters: '',
        OrderStatus: DOWNLOAD_ORDER_STATUSES,
        LinesPerPage: DOWNLOAD_PAGE_SIZE,
        PageNumber: 1,
        Filters: filterParams,
        Sorting: sortParams,
        FormatType: 'ORDER',
        Permission: true,
      };

      const { data } = await axiosInstance.post('/download', payload, {
        headers: jsonHeaders(),
        responseType: 'blob',
      });

      const blob = data instanceof Blob ? data : new Blob([data]);
      const filename = `orders_table_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      saveAs(blob, filename);
    } catch (error) {
      logger.error('Orders export failed', error as Error);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleUploadComplete = (result: UploadDialogResult) => {
    const hasError = !!result.errorMessage || (Array.isArray(result.errors) && result.errors.length > 0);
    if (!hasError) {
      refetch();
    }
  };

  const renderCell = (columnId: string, order: OrderListRow, index: number) => {
    const rowKey = getOrderKey(order) || `row-${index}`;
    const rowUpdating = isRowUpdating(rowKey);

    switch (columnId) {
      case 'orderNumber':
        return (
          <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>
            <Button
              component={RouterLink}
              to={`/orders/${normalizeOrderIdentifier(order.orderNumber ?? order.orderId)}`}
              size="small"
              sx={{
                p: 0,
                minWidth: 0,
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: '12px',
                letterSpacing: '.0178571429em',
                lineHeight: 'inherit',
                WebkitFontSmoothing: 'antialiased',
                color: ORDER_LINK_COLOR,
                textDecoration: 'none !important',
                '&:hover': { textDecoration: 'none !important', color: ORDER_LINK_COLOR },
                pointerEvents: rowUpdating ? 'none' : 'auto',
              }}
            >
              {normalizeOrderIdentifier(order.orderNumber ?? order.orderId)}
            </Button>
          </TableCell>
        );
      case 'preCyberOrderNumber':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{normalizeOrderIdentifier(order.preCyberOrderNumber)}</TableCell>;
      case 'creatorId':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{getOrderCreator(order) || '-'}</TableCell>;
      case 'orderType':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{order.orderTypeDescription ?? order.orderType}</TableCell>;
      case 'orderDate':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{formatDate(getOrderPlacedDateValue(order))}</TableCell>;
      case 'movementType':
        return (
          <TableCell key={`${rowKey}-${columnId}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, position: 'relative' }}>
              {movementIcon(order)}
              <Tooltip
                title={getMovementTypeLabel(order)}
                disableHoverListener={!shouldShowTooltip(getMovementTypeLabel(order), 15)}
              >
                <Typography variant="body2" sx={{ mt: 0, fontSize: '12px', lineHeight: 1.35 }}>
                  {truncate(getMovementTypeLabel(order), 15)}
                </Typography>
              </Tooltip>
            </Box>
          </TableCell>
        );
      case 'contactName':
        return (
          <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>
            <Tooltip
              title={getSoldToNameValue(order)}
              disableHoverListener={!shouldShowTooltip(getSoldToNameValue(order))}
            >
              <span>{truncate(getSoldToNameValue(order))}</span>
            </Tooltip>
          </TableCell>
        );
      case 'shipToPlantCode':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{getSupplyPointValue(order) || '-'}</TableCell>;
      case 'shipToNumber':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{getShipToCodeValue(order) || '-'}</TableCell>;
      case 'shipToPlantCity':
        return (
          <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>
            <Tooltip
              title={getShipToCityValue(order)}
              disableHoverListener={!shouldShowTooltip(getShipToCityValue(order), 12)}
            >
              <span>{truncate(getShipToCityValue(order), 12)}</span>
            </Tooltip>
          </TableCell>
        );
      case 'requestedDeliveryDate':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{formatDate(getRequestedDeliveryDateValue(order))}</TableCell>;
      case 'orderStatus':
        {
          const effectiveStatus = statusOverrides[rowKey] ?? getOrderStatusValue(order);

        return (
          <TableCell key={`${rowKey}-${columnId}`} sx={{ pr: '5px !important', minWidth: 165 }}>
            <StatusSelect
              id={`qa-orderStatus-sel-${rowKey || index}`}
              ariaLabel={`Order status for order ${normalizeOrderIdentifier(order.orderNumber ?? order.orderId)}`}
              value={effectiveStatus}
              options={orderStatuses}
              onChange={(status) => handleStatusChange(order, status)}
              updating={rowUpdating}
            />
          </TableCell>
        );
        }
      case 'statusDate':
        return <TableCell key={`${rowKey}-${columnId}`} sx={{ whiteSpace: 'nowrap' }}>{formatDate(statusDateOverrides[rowKey] ?? getStatusDateValue(order))}</TableCell>;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 0, m: 0 }}>
      <Toolbar
        disableGutters
        sx={{
          mb: 2,
          gap: 2,
          minHeight: '80px',
          height: '64px',
          width: '100%',
          display: 'flex',
          boxSizing: 'border-box',
          px: '16px',
          py: 0,
          backgroundColor: '#0c479d !important',
          color: '#fff !important',
          alignItems: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          sx={{
            color: '#fff',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '32px',
            letterSpacing: '.0125em',
          }}
        >
          Orders
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          id="qa-uploadOrders-btn"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
          sx={PAGE_HEADER_BUTTON_SX}
        >
          UPLOAD ORDERS
        </Button>
        <Button
          id="qa-newOrder-btn"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => create('orders')}
          sx={PAGE_HEADER_BUTTON_SX}
        >
          NEW ORDER
        </Button>
      </Toolbar>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {statusChangeError && (
          <Alert severity="error" onClose={() => setStatusChangeError(null)} sx={{ mx: 2, mt: 2, mb: 0 }}>
            {statusChangeError}
          </Alert>
        )}
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 1, md: 2 },
            pt: 0,
            pb: '4px',
            gap: 1.5,
            flexWrap: 'wrap',
            width: '100%',
            height: '65px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
        <Button
          id="qa-refreshTable-btn"
          variant="outlined"
          onClick={() => void handleRefreshTable()}
          disabled={isRefreshing}
          sx={OUTLINED_ACTION_BUTTON_SX}
        >
          REFRESH TABLE
        </Button>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <SortFilterControl
            mode="sort"
            fields={sortFilterFields}
            activeFields={activeSortFields}
            onApply={handleSortFilterApply}
            buttonId="qa-table-sort-btn"
            buttonSx={OUTLINED_ACTION_BUTTON_SX}
          />
          <SortFilterControl
            mode="filter"
            fields={sortFilterFields}
            activeFields={activeFilterFields}
            onApply={handleFilterApply}
            buttonId="qa-table-filter-btn"
            buttonSx={OUTLINED_ACTION_BUTTON_SX}
          />
          <LoadingButton
            id="qa-export-btn"
            variant="contained"
            disableElevation
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            loading={isExtracting}
            loadingText="EXPORTING"
            sx={{
              maxHeight: '36px',
              letterSpacing: '0.08em',
              color: 'common.white',
              backgroundColor: '#0c479d !important',
              transition: 'none !important',
              animation: 'none !important',
            }}
          >
            EXPORT TABLE
          </LoadingButton>
        </Box>
      </Toolbar>
        {isTableLoading && <TableNotification type="Loading" onTryAgainEvent={() => refetch()} />}
        {isError   && <TableNotification type="Error" onTryAgainEvent={() => refetch()} />}
        {!isTableLoading && !isError && total === 0 && (
          <TableNotification type={activeFilterFields.length > 0 ? 'Filter' : 'Empty'} />
        )}

        {!isTableLoading && total > 0 && (
          <>
            <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  width: '100%',
                  minWidth: 0,
                  border: 0,
                  borderSpacing: 0,
                  tableLayout: 'fixed',
                  whiteSpace: 'normal',
                  backgroundColor: 'white',
                  '& .MuiTableHead-root': {
                    borderSpacing: 0,
                    whiteSpace: 'normal',
                    background: 'inherit',
                    fontFamily: 'Roboto, sans-serif',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    color: 'var(--mat-table-header-headline-color, var(--mat-sys-on-surface, rgba(0, 0, 0, 0.87)))',
                    fontFamily: 'var(--mat-table-header-headline-font, var(--mat-sys-title-small-font, Roboto, sans-serif))',
                    fontSize: '11px',
                    letterSpacing: 'var(--mat-table-header-headline-tracking, var(--mat-sys-title-small-tracking, .0071428571em))',
                    borderBottomColor: 'var(--mat-table-row-item-outline-color, var(--mat-sys-outline, rgba(0, 0, 0, 0.12)))',
                    borderBottomWidth: 'var(--mat-table-row-item-outline-width, 1px)',
                    borderBottomStyle: 'solid',
                    boxSizing: 'border-box',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    textAlign: 'left',
                    background: 'inherit',
                    whiteSpace: 'nowrap',
                    padding: '0 4px !important',
                    outline: 'none',
                    WebkitFontSmoothing: 'antialiased',
                    fontWeight: 500,
                    lineHeight: 'inherit',
                  },
                  '& #qa-orders-preCyberOrderNumber-hdr, & #qa-orders-movementType-hdr, & #qa-orders-requestedDeliveryDate-hdr': {
                    textOverflow: 'clip !important',
                    overflow: 'visible !important',
                    whiteSpace: 'nowrap !important',
                    overflowWrap: 'normal !important',
                    wordBreak: 'normal !important',
                    lineHeight: 'inherit',
                  },
                  '& .MuiTableBody-root .MuiTableCell-root': {
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    lineHeight: 1.35,
                    textAlign: 'left',
                    color: 'rgba(0, 0, 0, 0.87)',
                    padding: '2px 4px',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '11px',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {AVAILABLE_COLUMN_IDS.map((columnId) => {
                      const column = COLUMNS_BY_ID[columnId];
                      const sortState = getSortStateForField(column.id);
                      const columnWidthSx =
                        column.id === 'orderStatus'
                          ? { minWidth: 140, width: '12%' }
                          : column.id === 'movementType'
                            ? { minWidth: 120, width: '10%' }
                            : column.id === 'requestedDeliveryDate'
                              ? { minWidth: 115, width: '9%' }
                              : undefined;
                      const noEllipsisHeaderSx = (column.id === 'movementType' || column.id === 'requestedDeliveryDate')
                        ? {
                          whiteSpace: 'nowrap',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          lineHeight: 'inherit',
                        }
                        : undefined;
                      return (
                      <TableCell
                        key={column.id}
                        id={HEADER_IDS_BY_COLUMN_ID[column.id] ?? `qa-orders-${column.id}-hdr`}
                        sx={{
                          ...(isFieldActive(column.id) ? { color: '#0c479d', fontWeight: 700 } : {}),
                          ...columnWidthSx,
                          ...noEllipsisHeaderSx,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <span>{column.label}</span>
                          {sortState.active && sortState.direction && (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.25,
                                color: '#0c479d',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              <span aria-hidden="true">{sortState.direction === 'asc' ? '↑' : '↓'}</span>
                              {activeSortFields.length > 1 && sortState.priority ? (
                                <Box
                                  component="span"
                                  sx={{
                                    minWidth: 14,
                                    height: 14,
                                    px: 0.25,
                                    borderRadius: '999px',
                                    backgroundColor: '#0c479d',
                                    color: '#fff',
                                    fontSize: '10px',
                                    lineHeight: '14px',
                                    textAlign: 'center',
                                  }}
                                >
                                  {sortState.priority}
                                </Box>
                              ) : null}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedOrders.map((order, index) => (
                    (() => {
                      const rowKey = getOrderKey(order) || `row-${index}`;
                      const rowUpdating = isRowUpdating(rowKey);

                      return (
                    <TableRow
                      key={`${rowKey}-${index}`}
                      hover
                      className="element-row"
                      aria-busy={rowUpdating}
                      sx={{
                        opacity: rowUpdating ? 0.55 : 1,
                        cursor: rowUpdating ? 'not-allowed' : 'default',
                        pointerEvents: rowUpdating ? 'none' : 'auto',
                        backgroundColor: rowUpdating ? 'rgba(12, 71, 157, 0.06)' : undefined,
                        '&:hover': {
                          backgroundColor: rowUpdating ? 'rgba(12, 71, 157, 0.06)' : ORDER_ROW_HOVER_BG,
                        },
                      }}
                    >
                      {AVAILABLE_COLUMN_IDS.map((columnId) => renderCell(columnId, order, index))}
                    </TableRow>
                      );
                    })()
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              labelRowsPerPage="Items per page:"
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              showFirstButton
              showLastButton
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRPP(Number(e.target.value)); setPage(0); }}
            />
          </>
        )}
      </Paper>

      <UploadDialog
        open={uploadOpen}
        source={getBusinessUnit()}
        userId={userData.userId}
        fileNamePrefix="bulk-order-template"
        onClose={() => setUploadOpen(false)}
        onComplete={handleUploadComplete}
      />

      <Dialog
        open={emptyRouteDialogOpen}
        onClose={() => setEmptyRouteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Empty route code</DialogTitle>
        <DialogContent>
          <Typography id="qa-emptyRoute-message-lbl">{EMPTY_ROUTE_ERROR_MESSAGE}</Typography>
        </DialogContent>
        <DialogActions>
          <Button id="qa-emptyRoute-okay-btn" onClick={() => setEmptyRouteDialogOpen(false)}>
            OKAY
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendingStatusNoteChange}
        onClose={handleNoteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="qa-title-lbl">
          Set Order #{pendingStatusNoteChange?.order.orderNumber ?? pendingStatusNoteChange?.order.orderId} to {pendingStatusNoteChange?.status}?
        </DialogTitle>
        <DialogContent>
          <Typography id="qa-paragraph-txt" sx={{ mb: 2, mt: 1 }}>
            Setting this shipment to {pendingStatusNoteChange?.status} will send the order back to the customer service representative. Please add a note to let the representative know why this order needs to be recycled.
          </Typography>
          <TextField
            id="qa-notes-ipt"
            fullWidth
            multiline
            minRows={3}
            autoFocus
            placeholder="Enter notes for this action"
            value={statusNote}
            onChange={(event) => setStatusNote(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button id="qa-cancel-btn" onClick={handleNoteDialogClose} disabled={!isOrderReadyAfterStatusChange}>
            CANCEL
          </Button>
          <LoadingButton
            id="qa-setStatus-btn"
            variant="contained"
            disableElevation
            onClick={() => void handleNoteDialogSubmit()}
            loading={!isOrderReadyAfterStatusChange}
            disabled={!statusNote.trim()}
          >
            {pendingStatusNoteChange ? `SET TO ${pendingStatusNoteChange.status.toUpperCase()}` : 'SET STATUS'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
